import Phaser from 'phaser';
import { activePalette } from './palette';

/**
 * Add a smooth radial-glow image centered at (x, y).
 *
 * Phaser's Graphics has no radial-gradient fill, so stacking translucent
 * circles is the usual workaround — but that bands visibly. Instead we bake a
 * one-off canvas texture with a real `createRadialGradient` (buttery falloff)
 * and cache it by key so repeated scene starts reuse the same texture.
 */
export function addRadialGlow(
  scene: Phaser.Scene,
  x: number,
  y: number,
  radius: number,
  color: number,
  alpha = 0.5,
  depth = -99,
): Phaser.GameObjects.Image {
  const key = `glow-${color.toString(16)}-${Math.round(radius)}`;
  if (!scene.textures.exists(key)) {
    const size = Math.max(2, Math.ceil(radius * 2));
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      const r = size / 2;
      const cr = (color >> 16) & 0xff;
      const cg = (color >> 8) & 0xff;
      const cb = color & 0xff;
      const grad = ctx.createRadialGradient(r, r, 0, r, r, r);
      grad.addColorStop(0, `rgba(${cr},${cg},${cb},1)`);
      grad.addColorStop(0.6, `rgba(${cr},${cg},${cb},0.35)`);
      grad.addColorStop(1, `rgba(${cr},${cg},${cb},0)`);
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, size, size);
    }
    scene.textures.addCanvas(key, canvas);
  }
  return scene.add.image(x, y, key).setAlpha(alpha).setDepth(depth);
}

/**
 * The shared "candy-shop" backdrop: a soft warm violet -> deep plum vertical
 * gradient, a gentle warm radial glow, and (optionally) a few slow-drifting
 * pastel bokeh orbs. Softer and sweeter than the old neon-navy look; used by
 * every scene so the whole app reads as one warm, sugary lit space.
 *
 * Skip `bokeh` on scenes whose backdrop is rebuilt repeatedly (e.g. Settings
 * toggles) — the drifting tweens would accumulate across rebuilds.
 */
export function addNeonBackdrop(
  scene: Phaser.Scene,
  opts: { glowX?: number; glowY?: number; glowRadius?: number; bokeh?: boolean; depth?: number } = {},
): void {
  const { width, height } = scene.scale;
  const depth = opts.depth ?? -100;
  const pal = activePalette();
  const bg = scene.add.graphics().setDepth(depth);
  // Vertical chrome gradient: a lifted top tone easing into a deep bottom tone.
  // Both stops come from the active palette (see util/palette.ts).
  bg.fillGradientStyle(pal.bgTop, pal.bgTop, pal.bgBottom, pal.bgBottom, 1, 1, 1, 1);
  bg.fillRect(0, 0, width, height);

  // Ambient radial glow behind the action — palette-tinted.
  addRadialGlow(
    scene,
    opts.glowX ?? width / 2,
    opts.glowY ?? height * 0.4,
    opts.glowRadius ?? Math.min(width, height) * 0.6,
    pal.glow,
    0.32,
    depth + 1,
  );

  if (opts.bokeh) {
    // Slow-drifting decorative orbs, tinted to the palette's bokeh set.
    const bokehColors = pal.bokeh;
    for (let i = 0; i < 5; i++) {
      const orb = scene.add
        .circle((width * (i + 0.5)) / 5, height * (0.14 + 0.16 * i), 30 + i * 12, bokehColors[i], 0.07)
        .setDepth(depth + 2);
      scene.tweens.add({
        targets: orb,
        y: orb.y + (i % 2 === 0 ? 44 : -44),
        x: orb.x + (i % 2 === 0 ? -26 : 26),
        duration: 6000 + i * 1300,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
    }
  }
}
